import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { Menu, X } from 'lucide-react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { NAV_ITEMS, SITE } from '../config/site'

export default function Header() {
  const [scrolled, setScrolled] = useState(false)
  const [open, setOpen] = useState(false)
  const location = useLocation()

  const isHome = location.pathname === '/'
  const isSolid = isHome ? (scrolled || open) : true

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    setOpen(false)
  }, [location.pathname])

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [open])

  return (
    <>
      <header className={`site-header ${isSolid ? 'site-header--solid' : ''}`}>
        <div className="container site-header__inner">
          <Link to="/" className="brand" aria-label={`${SITE.name}, accueil`}>
            <span className="brand__mark" aria-hidden="true">R</span>
            <span className="brand__text">
              <strong>Royaume des</strong>
              <span>Sacrificateurs</span>
            </span>
          </Link>

          <nav className="desktop-nav" aria-label="Navigation principale">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) => `nav-link ${isActive ? 'nav-link--active' : ''}`}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <button
            type="button"
            className="menu-toggle"
            aria-expanded={open}
            aria-controls="menu-mobile"
            aria-label={open ? 'Fermer le menu' : 'Ouvrir le menu'}
            onClick={() => setOpen((value) => !value)}
          >
            {open ? <X size={26} strokeWidth={1.7} /> : <Menu size={27} strokeWidth={1.7} />}
          </button>
        </div>
      </header>

      {createPortal(
        <AnimatePresence>
          {open && (
            <motion.div
              id="menu-mobile"
              className="mobile-menu"
              initial={{ opacity: 0, y: -16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="mobile-menu__top">
                <Link to="/" className="brand" aria-label={`${SITE.name}, accueil`} onClick={() => setOpen(false)}>
                  <span className="brand__mark" aria-hidden="true">R</span>
                  <span className="brand__text">
                    <strong>Royaume des</strong>
                    <span>Sacrificateurs</span>
                  </span>
                </Link>
                <button
                  type="button"
                  className="menu-toggle"
                  aria-label="Fermer le menu"
                  onClick={() => setOpen(false)}
                >
                  <X size={26} strokeWidth={1.7} />
                </button>
              </div>

              <nav aria-label="Navigation mobile" className="mobile-menu__nav">
                {NAV_ITEMS.map((item, index) => (
                  <motion.div
                    key={item.to}
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.04 + index * 0.035, duration: 0.28 }}
                  >
                    <NavLink
                      to={item.to}
                      end={item.to === '/'}
                      className={({ isActive }) => isActive ? 'mobile-menu__link mobile-menu__link--active' : 'mobile-menu__link'}
                    >
                      {item.label}
                    </NavLink>
                  </motion.div>
                ))}
              </nav>
              <div className="mobile-menu__footer">
                <a href={`tel:${SITE.phoneHref}`}>{SITE.phone}</a>
                <a href={`mailto:${SITE.email}`}>{SITE.email}</a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </>
  )
}